import { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector } from '../store/hooks';

/**
 * Generalized Piper chat state machine driven by a transport.
 *
 * Transport shape (all async):
 * {
 *   scopeKey: string,                 // history identity (loanId or 'scenario')
 *   streamChat({ message, intent, token, signal, onContent, onStatus, onMessageId })
 *   regenerate({ token, signal, onContent, onStatus, onMessageId })
 *   getHistory(token) → { messages, conversationKey }
 *   clear(token)
 *   telemetry({ eventType, intent, presetKey, conversationKey, metadata, token })
 *   feedback({ conversationKey, messageId, rating, token })
 *   getPresets?(token) → { categories }   // optional — loan chat only
 *   enrichMessage?(text, intent, presetKey, token) → string  // optional scenario enrichment
 *   clientPresets?: categories[]          // optional — scenario chat static presets
 * }
 */
export default function usePiperChat({
  transport,
  isOpen,
  quickActionsEnabled = true,
  quickActionsExpanded = false,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [presetCategories, setPresetCategories] = useState([]);
  const [presetsError, setPresetsError] = useState(null);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState('');
  const [selectedPresetKey, setSelectedPresetKey] = useState('');
  const [conversationKey, setConversationKey] = useState('');
  const [feedbackByMessageId, setFeedbackByMessageId] = useState({});
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const token = useAppSelector((state) => state.auth.token);

  const scopeKey = transport?.scopeKey ?? null;

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Reset local chat state when the transport scope changes (loan ↔ scenario, loan A ↔ B).
  useEffect(() => {
    setMessages([]);
    setHistoryLoaded(null);
    setConversationKey('');
    setFeedbackByMessageId({});
    setPresetCategories([]);
    setHistoryError(null);
    setPresetsError(null);
    setError(null);
  }, [scopeKey]);

  useEffect(() => {
    if (!isOpen) return;
    if (!scopeKey || !token || !transport?.getHistory) return;
    if (historyLoaded === scopeKey) return;

    const loadHistory = async () => {
      setIsLoadingHistory(true);
      setHistoryError(null);
      setError(null);
      try {
        const data = await transport.getHistory(token);
        setMessages((data.messages || []).map((m) => ({ id: m.id, role: m.role, content: m.content })));
        setConversationKey(data.conversationKey || '');
      } catch (err) {
        console.error('Failed to load chat history:', err);
        setHistoryError('Could not load the previous conversation.');
      } finally {
        setHistoryLoaded(scopeKey);
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [isOpen, scopeKey, token, historyLoaded, transport]);

  useEffect(() => {
    if (!quickActionsEnabled) return;
    if (!quickActionsExpanded) return;
    if (!isOpen) return;
    if (!scopeKey || !token) return;
    if (presetCategories.length > 0) return;

    if (Array.isArray(transport?.clientPresets) && transport.clientPresets.length > 0) {
      setPresetCategories(transport.clientPresets);
      return;
    }

    if (!transport?.getPresets) return;

    const loadPresets = async () => {
      setIsLoadingPresets(true);
      setPresetsError(null);
      try {
        const data = await transport.getPresets(token);
        setPresetCategories(data.categories || []);
      } catch (err) {
        console.error('Failed to load AI chat presets:', err);
        setPresetsError('Could not load quick actions.');
      } finally {
        setIsLoadingPresets(false);
      }
    };

    loadPresets();
  }, [
    isOpen, scopeKey, token, presetCategories.length,
    quickActionsEnabled, quickActionsExpanded, transport,
  ]);

  const trackTelemetry = useCallback(async (eventType, metadata = {}) => {
    if (!scopeKey || !token || !transport?.telemetry) return;
    try {
      await transport.telemetry({
        eventType,
        intent: selectedIntent,
        presetKey: selectedPresetKey,
        conversationKey,
        metadata,
        token,
      });
    } catch (telemetryErr) {
      console.error('Failed to send AI chat telemetry:', telemetryErr);
    }
  }, [scopeKey, token, selectedIntent, selectedPresetKey, conversationKey, transport]);

  const panelOpenedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !panelOpenedRef.current) {
      panelOpenedRef.current = true;
      trackTelemetry('panel_opened');
    }
    if (!isOpen) {
      panelOpenedRef.current = false;
    }
  }, [isOpen, trackTelemetry]);

  const submitFeedback = useCallback(async (rating, messageId) => {
    if (!scopeKey || !token || !transport?.feedback) return;
    try {
      await transport.feedback({ conversationKey, messageId, rating, token });
      if (messageId) {
        setFeedbackByMessageId((prev) => ({ ...prev, [messageId]: rating }));
      }
      trackTelemetry('feedback_submitted', { rating: String(rating), messageId: String(messageId || '') });
    } catch (feedbackErr) {
      console.error('Failed to submit AI feedback:', feedbackErr);
    }
  }, [scopeKey, token, conversationKey, trackTelemetry, transport]);

  const runStream = useCallback(async (startStream) => {
    setError(null);
    setStreamingContent('');
    setIsStreaming(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let accumulated = '';
    let messageId = null;

    try {
      await startStream({
        signal: abortController.signal,
        onContent: (delta) => {
          accumulated += delta;
          setStreamingContent(accumulated);
        },
        onStatus: (status) => setStatusMessage(status),
        onMessageId: (id) => {
          messageId = id;
        },
      });

      if (accumulated) {
        setMessages((prev) => [...prev, { id: messageId, role: 'assistant', content: accumulated }]);
        await trackTelemetry('response_received', { length: String(accumulated.length) });
      }
      return true;
    } catch (err) {
      if (err.name === 'AbortError') {
        if (accumulated) {
          setMessages((prev) => [...prev, { id: messageId, role: 'assistant', content: accumulated }]);
        }
      } else {
        setError(err.message || 'Failed to send message');
        console.error('AI Chat error:', err);
      }
      return false;
    } finally {
      setStreamingContent('');
      setStatusMessage('');
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [trackTelemetry]);

  const submitMessage = useCallback(async (text, intent, presetKey) => {
    const trimmed = (text || '').trim();
    if (!trimmed || isStreaming || !scopeKey || !transport?.streamChat) return;

    setMessages((prev) => [...prev, { id: null, role: 'user', content: trimmed }]);

    let aiMessage = trimmed;
    if (transport.enrichMessage) {
      aiMessage = await transport.enrichMessage(trimmed, intent, presetKey, token);
    }
    await trackTelemetry('message_sent');

    await runStream(({ signal, onContent, onStatus, onMessageId }) =>
      transport.streamChat({
        message: aiMessage,
        intent: intent || undefined,
        token,
        signal,
        onContent,
        onStatus,
        onMessageId,
      }));
  }, [isStreaming, scopeKey, token, trackTelemetry, runStream, transport]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isStreaming || !scopeKey) return;

    const text = input;
    const intent = selectedIntent;
    const presetKey = selectedPresetKey;
    setInput('');
    setSelectedIntent('');
    setSelectedPresetKey('');

    await submitMessage(text, intent, presetKey);
  }, [input, isStreaming, scopeKey, selectedIntent, selectedPresetKey, submitMessage]);

  const sendPreset = useCallback(async (promptTemplate, intent, presetKey) => {
    if (!promptTemplate || isStreaming || !scopeKey) return;
    trackTelemetry('preset_selected', { presetKey: presetKey || '' });
    await submitMessage(promptTemplate, intent || '', presetKey || '');
  }, [isStreaming, scopeKey, trackTelemetry, submitMessage]);

  const regenerate = useCallback(async () => {
    if (isStreaming || !scopeKey || !transport?.regenerate) return;
    if (!messages.some((m) => m.role === 'user')) return;

    setMessages((prev) => {
      const last = prev[prev.length - 1];
      return last?.role === 'assistant' ? prev.slice(0, -1) : prev;
    });

    await trackTelemetry('regenerate_requested');

    await runStream(({ signal, onContent, onStatus, onMessageId }) =>
      transport.regenerate({ token, signal, onContent, onStatus, onMessageId }));
  }, [isStreaming, scopeKey, messages, token, trackTelemetry, runStream, transport]);

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const clearChat = useCallback(async () => {
    if (isStreaming || !scopeKey || !token || !transport?.clear) return;
    setError(null);
    try {
      await transport.clear(token);
      setMessages([]);
      setFeedbackByMessageId({});
      setConversationKey('');
      trackTelemetry('conversation_cleared');
    } catch (err) {
      console.error('Failed to clear conversation:', err);
      setError('Could not clear the conversation.');
    }
  }, [isStreaming, scopeKey, token, trackTelemetry, transport]);

  const canRegenerate = !isStreaming
    && messages.length > 0
    && messages[messages.length - 1]?.role === 'assistant';

  return {
    messages,
    input,
    setInput,
    streamingContent,
    statusMessage,
    isStreaming,
    isLoadingHistory,
    isLoadingPresets,
    historyError,
    presetsError,
    error,
    presetCategories,
    feedbackByMessageId,
    canRegenerate,
    sendMessage,
    regenerate,
    stopStreaming,
    clearChat,
    sendPreset,
    submitFeedback,
  };
}
